import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حساب عدد المولات - حاسبة المولات الكيميائية بدقة عالية";
const DESCRIPTION =
  "حاسبة المولات الكيميائية المجانية - احسب عدد المولات من الكتلة والكتلة المولية، أو من عدد الجسيمات، أو من حجم الغاز. نتائج فورية ودقيقة للطلاب والمعلمين.";
const KEYWORDS =
  "حساب عدد المولات, حاسبة المولات, قانون عدد المولات, الكتلة المولية, المول الكيميائي, عدد أفوجادرو, حجم المول, حاسبة كيمياء, moles calculator, molar mass";
const OG_URL = "https://do-calculate.com/calculator/ar/moles-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة عدد المولات الكيميائية",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة عدد المولات",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كيف أحسب عدد المولات من الكتلة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "لحساب عدد المولات من الكتلة، استخدم القانون: عدد المولات = الكتلة (جرام) ÷ الكتلة المولية (جرام/مول). مثلاً: 36 جرام من الماء H₂O تُعطي 36 ÷ 18 = 2 مول.",
        },
      },
      {
        "@type": "Question",
        name: "ما هو عدد أفوجادرو؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "عدد أفوجادرو هو 6.022 × 10²³ جسيم/مول. يمثل عدد الجسيمات (ذرات أو جزيئات أو أيونات) في مول واحد من أي مادة.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحسب عدد المولات من حجم الغاز؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "عند درجة الحرارة والضغط القياسيين (STP)، يشغل المول الواحد من أي غاز مثالي 22.4 لتر. القانون: عدد المولات = الحجم (لتر) ÷ 22.4. مثلاً: 44.8 لتر من الأكسجين = 44.8 ÷ 22.4 = 2 مول.",
        },
      },
    ],
  },
];

// Avogadro's number
const AVOGADRO = 6.022e23;
// Molar volume at STP (L/mol)
const MOLAR_VOLUME_STP = 22.4;

// Calculation methods
const CALC_METHODS = {
  mass_to_moles: "من الكتلة والكتلة المولية",
  particles_to_moles: "من عدد الجسيمات",
  volume_to_moles: "من حجم الغاز (STP)",
  moles_to_mass: "من المولات إلى الكتلة",
  moles_to_particles: "من المولات إلى عدد الجسيمات",
  moles_to_volume: "من المولات إلى حجم الغاز (STP)",
};

// Common substances with molar masses (g/mol)
const COMMON_SUBSTANCES = {
  custom: { name: "مادة مخصصة", molarMass: 0 },
  H2O: { name: "الماء H₂O", molarMass: 18.015 },
  NaCl: { name: "كلوريد الصوديوم NaCl", molarMass: 58.44 },
  CO2: { name: "ثاني أكسيد الكربون CO₂", molarMass: 44.01 },
  O2: { name: "الأكسجين O₂", molarMass: 32.0 },
  H2: { name: "الهيدروجين H₂", molarMass: 2.016 },
  N2: { name: "النيتروجين N₂", molarMass: 28.014 },
  CH4: { name: "الميثان CH₄", molarMass: 16.043 },
  H2SO4: { name: "حمض الكبريتيك H₂SO₄", molarMass: 98.079 },
  HCl: { name: "حمض الهيدروكلوريك HCl", molarMass: 36.461 },
  NaOH: { name: "هيدروكسيد الصوديوم NaOH", molarMass: 40.0 },
  CaCO3: { name: "كربونات الكالسيوم CaCO₃", molarMass: 100.087 },
  C6H12O6: { name: "الجلوكوز C₆H₁₂O₆", molarMass: 180.156 },
  Fe: { name: "الحديد Fe", molarMass: 55.845 },
  Cu: { name: "النحاس Cu", molarMass: 63.546 },
  Al: { name: "الألمنيوم Al", molarMass: 26.982 },
  Ca: { name: "الكالسيوم Ca", molarMass: 40.078 },
  Mg: { name: "المغنيسيوم Mg", molarMass: 24.305 },
  C: { name: "الكربون C", molarMass: 12.011 },
  Na: { name: "الصوديوم Na", molarMass: 22.99 },
};

function MolesCalculator() {
  const [calcMethod, setCalcMethod] = useState("mass_to_moles");
  const [substance, setSubstance] = useState("H2O");
  const [customMolarMass, setCustomMolarMass] = useState("");

  // Inputs
  const [mass, setMass] = useState("");
  const [moles, setMoles] = useState("");
  const [particles, setParticles] = useState("");
  const [volume, setVolume] = useState("");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const getMolarMass = () => {
    if (substance === "custom") return parseFloat(customMolarMass || 0);
    return COMMON_SUBSTANCES[substance]?.molarMass || 0;
  };

  const formatScientific = (num) => {
    if (num === 0) return "0";
    if (Math.abs(num) >= 1e6 || Math.abs(num) < 0.0001) {
      return num.toExponential(4);
    }
    return num.toLocaleString("en-US", { maximumFractionDigits: 6 });
  };

  const calculateResult = () => {
    try {
      setError("");
      const molarMass = getMolarMass();
      let resultData = null;

      switch (calcMethod) {
        case "mass_to_moles": {
          const massVal = parseFloat(mass);
          if (!mass || isNaN(massVal) || massVal <= 0) {
            setError("يرجى إدخال كتلة صحيحة وموجبة");
            setResult(null);
            return;
          }
          if (molarMass <= 0) {
            setError("يرجى إدخال كتلة مولية صحيحة");
            setResult(null);
            return;
          }
          const molesResult = massVal / molarMass;
          const particlesResult = molesResult * AVOGADRO;
          const volumeResult = molesResult * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesResult,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massVal,
            molarMass,
            formula: `عدد المولات = ${massVal} جم ÷ ${molarMass} جم/مول = ${molesResult.toFixed(6)} مول`,
          };
          break;
        }

        case "particles_to_moles": {
          const particlesVal = parseFloat(particles);
          if (!particles || isNaN(particlesVal) || particlesVal <= 0) {
            setError("يرجى إدخال عدد صحيح وموجب للجسيمات");
            setResult(null);
            return;
          }
          const molesResult = particlesVal / AVOGADRO;
          const massResult = molarMass > 0 ? molesResult * molarMass : null;
          const volumeResult = molesResult * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesResult,
            particles: particlesVal,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `عدد المولات = ${particlesVal.toExponential(3)} ÷ 6.022×10²³ = ${molesResult.toExponential(4)} مول`,
          };
          break;
        }

        case "volume_to_moles": {
          const volumeVal = parseFloat(volume);
          if (!volume || isNaN(volumeVal) || volumeVal <= 0) {
            setError("يرجى إدخال حجم صحيح وموجب");
            setResult(null);
            return;
          }
          const molesResult = volumeVal / MOLAR_VOLUME_STP;
          const particlesResult = molesResult * AVOGADRO;
          const massResult = molarMass > 0 ? molesResult * molarMass : null;
          resultData = {
            moles: molesResult,
            particles: particlesResult,
            volumeSTP: volumeVal,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `عدد المولات = ${volumeVal} لتر ÷ 22.4 لتر/مول = ${molesResult.toFixed(6)} مول`,
          };
          break;
        }

        case "moles_to_mass": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("يرجى إدخال عدد مولات صحيح وموجب");
            setResult(null);
            return;
          }
          if (molarMass <= 0) {
            setError("يرجى إدخال كتلة مولية صحيحة");
            setResult(null);
            return;
          }
          const massResult = molesVal * molarMass;
          const particlesResult = molesVal * AVOGADRO;
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass,
            formula: `الكتلة = ${molesVal} مول × ${molarMass} جم/مول = ${massResult.toFixed(4)} جم`,
          };
          break;
        }

        case "moles_to_particles": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("يرجى إدخال عدد مولات صحيح وموجب");
            setResult(null);
            return;
          }
          const particlesResult = molesVal * AVOGADRO;
          const massResult = molarMass > 0 ? molesVal * molarMass : null;
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `عدد الجسيمات = ${molesVal} × 6.022×10²³ = ${particlesResult.toExponential(4)}`,
          };
          break;
        }

        case "moles_to_volume": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("يرجى إدخال عدد مولات صحيح وموجب");
            setResult(null);
            return;
          }
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          const particlesResult = molesVal * AVOGADRO;
          const massResult = molarMass > 0 ? molesVal * molarMass : null;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `الحجم عند STP = ${molesVal} مول × 22.4 لتر/مول = ${volumeResult.toFixed(4)} لتر`,
          };
          break;
        }

        default:
          throw new Error("طريقة حساب غير معروفة");
      }

      resultData.substanceName =
        substance === "custom"
          ? "مادة مخصصة"
          : COMMON_SUBSTANCES[substance]?.name || "";
      resultData.methodName = CALC_METHODS[calcMethod];
      setResult(resultData);
    } catch (err) {
      setError("خطأ في الحساب: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    const hasInput =
      mass || moles || particles || volume;
    if (hasInput) {
      calculateResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcMethod, substance, customMolarMass, mass, moles, particles, volume]);

  const handleClear = () => {
    setMass("");
    setMoles("");
    setParticles("");
    setVolume("");
    setCustomMolarMass("");
    setResult(null);
    setError("");
  };

  const renderInputFields = () => {
    switch (calcMethod) {
      case "mass_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label >الكتلة (جرام)</label>
              <input
                type="number"
                className="form-control"
                value={mass}
                onChange={(e) => setMass(e.target.value)}
                min="0"
                step="0.001"
                placeholder="أدخل الكتلة بالجرام"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <label >الكتلة المولية (جرام/مول)</label>
              <input
                type="number"
                className="form-control"
                value={getMolarMass() || ""}
                readOnly={substance !== "custom"}
                onChange={(e) =>
                  substance === "custom" && setCustomMolarMass(e.target.value)
                }
                min="0"
                step="0.001"
                placeholder="الكتلة المولية"
                style={
                  substance !== "custom"
                    ? { backgroundColor: "#f0f9f4" }
                    : { backgroundColor: "#fff3cd" }
                }
              />
            </div>
          </div>
        );

      case "particles_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >
                عدد الجسيمات (ذرات / جزيئات / أيونات)
              </label>
              <input
                type="number"
                className="form-control"
                value={particles}
                onChange={(e) => setParticles(e.target.value)}
                min="0"
                step="any"
                placeholder="أدخل عدد الجسيمات (مثال: 3.011e23)"
              />
              <small className="text-muted">
                عدد أفوجادرو = 6.022 × 10²³ جسيم/مول
              </small>
            </div>
          </div>
        );

      case "volume_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >حجم الغاز عند STP (لتر)</label>
              <input
                type="number"
                className="form-control"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                min="0"
                step="0.001"
                placeholder="أدخل الحجم باللتر"
              />
              <small className="text-muted">
                الحجم المولي عند STP = 22.4 لتر/مول (0°م، 1 ضغط جوي)
              </small>
            </div>
          </div>
        );

      case "moles_to_mass":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label >عدد المولات (مول)</label>
              <input
                type="number"
                className="form-control"
                value={moles}
                onChange={(e) => setMoles(e.target.value)}
                min="0"
                step="0.0001"
                placeholder="أدخل عدد المولات"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <label >الكتلة المولية (جرام/مول)</label>
              <input
                type="number"
                className="form-control"
                value={getMolarMass() || ""}
                readOnly={substance !== "custom"}
                onChange={(e) =>
                  substance === "custom" && setCustomMolarMass(e.target.value)
                }
                min="0"
                step="0.001"
                placeholder="الكتلة المولية"
                style={
                  substance !== "custom"
                    ? { backgroundColor: "#f0f9f4" }
                    : { backgroundColor: "#fff3cd" }
                }
              />
            </div>
          </div>
        );

      case "moles_to_particles":
      case "moles_to_volume":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >عدد المولات (مول)</label>
              <input
                type="number"
                className="form-control"
                value={moles}
                onChange={(e) => setMoles(e.target.value)}
                min="0"
                step="0.0001"
                placeholder="أدخل عدد المولات"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const needsMolarMass = [
    "mass_to_moles",
    "moles_to_mass",
    "particles_to_moles",
    "volume_to_moles",
    "moles_to_particles",
    "moles_to_volume",
  ].includes(calcMethod);

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>
              حاسبة عدد المولات الكيميائية
            </h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة عدد المولات</strong> أداة كيميائية مجانية ودقيقة
                تساعدك على{" "}
                <strong>حساب عدد المولات</strong> بثلاث طرق مختلفة: من الكتلة
                والكتلة المولية، أو من عدد الجسيمات باستخدام عدد أفوجادرو، أو من
                حجم الغاز عند الشروط القياسية. مثالية لطلاب الكيمياء والمعلمين
                والمختبرات العلمية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  احسب عدد المولات بسهولة
                </h2>
              </div>

              {/* Calculation Method */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="calcMethod" >
                    طريقة الحساب
                  </label>
                  <select
                    id="calcMethod"
                    className="form-select"
                    value={calcMethod}
                    onChange={(e) => {
                      setCalcMethod(e.target.value);
                      handleClear();
                    }}
                  >
                    {Object.entries(CALC_METHODS).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Substance Selection */}
              {needsMolarMass && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="substance" >
                      المادة الكيميائية
                    </label>
                    <select
                      id="substance"
                      className="form-select"
                      value={substance}
                      onChange={(e) => setSubstance(e.target.value)}
                    >
                      {Object.entries(COMMON_SUBSTANCES).map(
                        ([key, { name, molarMass }]) => (
                          <option key={key} value={key}>
                            {name}{" "}
                            {molarMass > 0
                              ? `(${molarMass} جم/مول)`
                              : "(أدخل الكتلة المولية)"}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              )}

              {/* Custom Molar Mass */}
              {substance === "custom" &&
                [
                  "mass_to_moles",
                  "moles_to_mass",
                  "particles_to_moles",
                  "volume_to_moles",
                  "moles_to_particles",
                  "moles_to_volume",
                ].includes(calcMethod) && (
                  <div className="row">
                    <div className="col-sm-12">
                      <label htmlFor="customMolarMass" >
                        الكتلة المولية المخصصة (جرام/مول)
                      </label>
                      <input
                        id="customMolarMass"
                        type="number"
                        className="form-control"
                        value={customMolarMass}
                        onChange={(e) => setCustomMolarMass(e.target.value)}
                        min="0"
                        step="0.001"
                        placeholder="أدخل الكتلة المولية للمادة"
                        style={{ backgroundColor: "#fff3cd" }}
                      />
                    </div>
                  </div>
                )}

              {/* Dynamic Input Fields */}
              {renderInputFields()}

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  امسح
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>

                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            الكمية
                          </th>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>عدد المولات</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {formatScientific(result.moles)} مول
                          </td>
                        </tr>
                        {result.mass !== null && result.mass !== undefined && (
                          <tr>
                            <td style={{ textAlign: "center" }}>الكتلة</td>
                            <td
                              style={{
                                textAlign: "center",
                                fontSize: "16px",
                                fontWeight: "bold",
                                color: "#0f3d28",
                              }}
                            >
                              {formatScientific(result.mass)} جرام
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ textAlign: "center" }}>عدد الجسيمات</td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>
                            {result.particles.toExponential(4)} جسيم
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            الحجم عند STP
                          </td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>
                            {formatScientific(result.volumeSTP)} لتر
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>نتائج إضافية</strong>
                      </div>
                      <div className="info-section-body">
                        <ul
                          style={{
                            listStyle: "none",
                            padding: 0,
                            lineHeight: "2",
                          }}
                        >
                          <li>
                            🧪 <strong>عدد المولات:</strong>{" "}
                            {formatScientific(result.moles)} مول
                          </li>
                          {result.mass !== null && result.mass !== undefined && (
                            <li>
                              ⚖️ <strong>الكتلة:</strong>{" "}
                              {formatScientific(result.mass)} جرام
                            </li>
                          )}
                          <li>
                            ⚛️ <strong>عدد الجسيمات:</strong>{" "}
                            {result.particles.toExponential(4)}
                          </li>
                          <li>
                            🫧 <strong>الحجم عند STP:</strong>{" "}
                            {formatScientific(result.volumeSTP)} لتر
                          </li>
                          {result.molarMass && (
                            <li>
                              📋 <strong>الكتلة المولية:</strong>{" "}
                              {result.molarMass} جرام/مول
                            </li>
                          )}
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 المادة:</strong> {result.substanceName}
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>طريقة الحساب:</strong> {result.methodName}
                          </p>
                          <p>
                            <strong>المادة:</strong> {result.substanceName}
                          </p>
                          {result.molarMass && (
                            <p>
                              <strong>الكتلة المولية:</strong>{" "}
                              {result.molarMass} جرام/مول
                            </p>
                          )}
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}>
                            <strong>خطوات الحساب:</strong>
                          </p>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              backgroundColor: "#f8f9fa",
                              padding: "10px",
                              borderRadius: "5px",
                            }}
                          >
                            {result.formula}
                          </p>
                          <p style={{ fontSize: "13px", color: "#666" }}>
                            عدد أفوجادرو = 6.022 × 10²³ جسيم/مول
                            <br />
                            الحجم المولي عند STP = 22.4 لتر/مول
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">

            {/* Section 1: What is it */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة عدد المولات؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>حاسبة عدد المولات</strong> أداة إلكترونية مجانية
                  تُحوّل بين الكتلة وعدد المولات وعدد الجسيمات وحجم الغاز في
                  لحظات. المول (Mole) هو وحدة القياس الأساسية في الكيمياء؛ يمثّل
                  عدداً محدداً من الجسيمات يساوي{" "}
                  <strong>6.022 × 10²³ جسيم</strong>، وهو ما يُعرف بـ
                  عدد أفوجادرو. استخدم هذه الحاسبة لـ:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حساب المولات من الكتلة:</strong> قسّم كتلة المادة
                    بالجرام على كتلتها المولية
                  </li>
                  <li>
                    <strong>التحويل من الجسيمات:</strong> قسّم عدد الجسيمات على
                    عدد أفوجادرو
                  </li>
                  <li>
                    <strong>حساب مولات الغاز:</strong> قسّم الحجم باللتر على
                    22.4 لتر/مول عند STP
                  </li>
                  <li>
                    <strong>العكس:</strong> حوّل المولات إلى كتلة أو جسيمات أو
                    حجم غاز
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام الحاسبة خطوة بخطوة:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر طريقة الحساب المناسبة من القائمة المنسدلة</li>
                  <li>
                    اختر المادة الكيميائية (أو "مادة مخصصة" لإدخال كتلة مولية
                    يدوية)
                  </li>
                  <li>أدخل القيمة المعطاة (كتلة، مولات، جسيمات، أو حجم)</li>
                  <li>تظهر النتيجة فوراً مع جميع التحويلات المرتبطة</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 ملاحظة:</strong> الحاسبة تعطيك في آنٍ واحد عدد
                  المولات والكتلة وعدد الجسيمات والحجم عند STP — وفّر وقتك
                  وتجنّب أخطاء الحساب اليدوي.
                </div>
              </div>
            </div>

            {/* Section 2: Use cases */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات حاسبة المولات</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  في الدراسة والامتحانات:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حل مسائل الكيمياء:</strong> احسب المولات بسرعة في
                    أسئلة المعادلات الكيميائية
                  </li>
                  <li>
                    <strong>مراجعة الامتحانات:</strong> تحقق من إجاباتك قبل
                    تسليم الورقة
                  </li>
                  <li>
                    <strong>فهم العلاقات الكيميائية:</strong> استوعب الصلة بين
                    الكتلة والمول والجسيمات
                  </li>
                  <li>
                    <strong>حل التمارين التدريجية:</strong> تدرّب على أنواع
                    مختلفة من المسائل
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في المختبرات العلمية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تحضير المحاليل:</strong> حدد كمية المادة المطلوبة
                    بدقة
                  </li>
                  <li>
                    <strong>حسابات التفاعلات:</strong> عرّف الكميات المتفاعلة
                    والمنتجة
                  </li>
                  <li>
                    <strong>قياس الغازات:</strong> تحويل حجم الغاز إلى مولات
                    بسهولة
                  </li>
                  <li>
                    <strong>توثيق التجارب:</strong> سجّل النتائج بوحدات قياسية
                    دقيقة
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في الصناعة الكيميائية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>ضبط نسب التفاعل:</strong> حساب كميات المواد الخام
                    بدقة
                  </li>
                  <li>
                    <strong>ضبط الجودة:</strong> التحقق من تركيز المنتجات
                    الكيميائية
                  </li>
                  <li>
                    <strong>تحسين الكفاءة:</strong> تقليل الهدر عبر حسابات
                    دقيقة
                  </li>
                  <li>
                    <strong>الوصفات الصناعية:</strong> ضبط نسب المكونات في
                    التصنيع
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في التدريس والتعليم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>شرح المفاهيم:</strong> عرض التحويلات بشكل مرئي
                    وواضح
                  </li>
                  <li>
                    <strong>التحقق الفوري:</strong> اختبار إجابات الطلاب على
                    المسائل
                  </li>
                  <li>
                    <strong>تصميم الأنشطة:</strong> توليد أمثلة متنوعة للتدريب
                  </li>
                  <li>
                    <strong>إعداد الاختبارات:</strong> التأكد من صحة أسئلة
                    الامتحانات
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 3: Educational content */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>قوانين حساب عدد المولات</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يرتبط المول بثلاث كميات قياسية أساسية: الكتلة، وعدد الجسيمات،
                  وحجم الغاز. كل قانون يصف علاقة محددة بين هذه الكميات:
                </p>
                <table
                  className="table bmi-table"
                  style={{ marginTop: "1rem" }}
                >
                  <thead>
                    <tr>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        القانون
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        الصيغة
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        المتغيرات
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>المولات من الكتلة</td>
                      <td style={{ textAlign: "center" }}>
                        n = m ÷ M
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: مولات، m: كتلة (جم)، M: كتلة مولية (جم/مول)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        المولات من الجسيمات
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n = N ÷ Nₐ
                      </td>
                      <td style={{ textAlign: "center" }}>
                        N: عدد الجسيمات، Nₐ = 6.022×10²³
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>المولات من الحجم</td>
                      <td style={{ textAlign: "center" }}>
                        n = V ÷ 22.4
                      </td>
                      <td style={{ textAlign: "center" }}>
                        V: الحجم باللتر عند STP (0°م، 1 ضغط جوي)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        الكتلة من المولات
                      </td>
                      <td style={{ textAlign: "center" }}>
                        m = n × M
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: مولات، M: كتلة مولية (جم/مول)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        الجسيمات من المولات
                      </td>
                      <td style={{ textAlign: "center" }}>
                        N = n × Nₐ
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: مولات، Nₐ = 6.022×10²³
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>الحجم من المولات</td>
                      <td style={{ textAlign: "center" }}>
                        V = n × 22.4
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: مولات، الحجم باللتر عند STP
                      </td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  ما هو المول تحديداً؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  المول ليس مجرد رقم — هو وحدة قياس للكمية كما أن الدزينة تعني
                  12. كل مول من أي مادة يحتوي على{" "}
                  <strong>6.022 × 10²³</strong> من جسيماتها. وهذا الرقم الضخم
                  ليس اعتباطياً: هو عدد الذرات الموجودة في 12 جراماً بالضبط من
                  الكربون-12. اختار العلماء هذا التعريف لربط الكيمياء
                  المجهرية (الذرات) بالقياسات المخبرية (الجرامات والمليلترات).
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  الكتلة المولية لبعض المواد الشائعة:
                </h3>
                <table
                  className="table bmi-table"
                  style={{ marginTop: "0.5rem" }}
                >
                  <thead>
                    <tr>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        المادة
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        الرمز
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        الكتلة المولية (جم/مول)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>الماء</td>
                      <td style={{ textAlign: "center" }}>H₂O</td>
                      <td style={{ textAlign: "center" }}>18.015</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        ثاني أكسيد الكربون
                      </td>
                      <td style={{ textAlign: "center" }}>CO₂</td>
                      <td style={{ textAlign: "center" }}>44.01</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        كلوريد الصوديوم (الملح)
                      </td>
                      <td style={{ textAlign: "center" }}>NaCl</td>
                      <td style={{ textAlign: "center" }}>58.44</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        حمض الكبريتيك
                      </td>
                      <td style={{ textAlign: "center" }}>H₂SO₄</td>
                      <td style={{ textAlign: "center" }}>98.079</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>الجلوكوز</td>
                      <td style={{ textAlign: "center" }}>C₆H₁₂O₆</td>
                      <td style={{ textAlign: "center" }}>180.156</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>الأكسجين</td>
                      <td style={{ textAlign: "center" }}>O₂</td>
                      <td style={{ textAlign: "center" }}>32.0</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ كيف تحسب الكتلة المولية؟</strong> اجمع الكتل
                  الذرية لجميع عناصر الجزيء. مثلاً: الماء H₂O = (2 × 1.008) +
                  16.0 = 18.016 جم/مول.
                </div>
              </div>
            </div>

            {/* Section 4: Practical examples */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على حساب عدد المولات</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: كم مولاً في 36 جرام من الماء؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المعطيات:</strong> كتلة الماء = 36 جم، الكتلة المولية
                  لـ H₂O = 18.015 جم/مول
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "من الكتلة والكتلة المولية"</li>
                  <li>اختر "الماء H₂O" من قائمة المواد</li>
                  <li>أدخل الكتلة: 36 جرام</li>
                  <li>النتيجة: 36 ÷ 18.015 ≈ 1.999 مول</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>التحقق:</strong> 2 مول × 18.015 = 36.03 جم ✓
                </div>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 2: كم مولاً في 3.011 × 10²³ ذرة؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المعطيات:</strong> عدد الجسيمات = 3.011 × 10²³ ذرة
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "من عدد الجسيمات"</li>
                  <li>أدخل: 3.011e23</li>
                  <li>
                    النتيجة: 3.011×10²³ ÷ 6.022×10²³ = <strong>0.5 مول</strong>
                  </li>
                </ol>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 3: كم مولاً في 44.8 لتر من ثاني أكسيد الكربون؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المعطيات:</strong> حجم CO₂ = 44.8 لتر عند STP
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "من حجم الغاز (STP)"</li>
                  <li>أدخل الحجم: 44.8 لتر</li>
                  <li>
                    النتيجة: 44.8 ÷ 22.4 = <strong>2 مول</strong>
                  </li>
                  <li>الكتلة المقابلة: 2 × 44.01 = 88.02 جم</li>
                </ol>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 4: كم جراماً يساوي 0.5 مول من ملح الطعام؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المعطيات:</strong> 0.5 مول من NaCl، كتلته المولية =
                  58.44 جم/مول
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "من المولات إلى الكتلة"</li>
                  <li>اختر "كلوريد الصوديوم NaCl"</li>
                  <li>أدخل: 0.5 مول</li>
                  <li>
                    النتيجة: 0.5 × 58.44 = <strong>29.22 جرام</strong>
                  </li>
                </ol>
              </div>
            </div>

            {/* Section 5: Tips */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح لحساب المولات بشكل صحيح</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح عامة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تعلّم الكتل الذرية:</strong> احفظ كتل العناصر
                    الشائعة (H=1، C=12، N=14، O=16، Na=23، Cl=35.5) لحساب
                    الكتلة المولية بسرعة
                  </li>
                  <li>
                    <strong>تحقق من الوحدات:</strong> الكتلة يجب أن تكون
                    بالجرام، والحجم باللتر عند STP
                  </li>
                  <li>
                    <strong>استخدم الكتابة العلمية:</strong> لأعداد الجسيمات
                    الكبيرة اكتب مثلاً 6.022e23
                  </li>
                  <li>
                    <strong>تحقق من نتيجتك:</strong> اضرب المولات في الكتلة
                    المولية، يجب أن تجد الكتلة الأصلية
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  أخطاء شائعة يجب تجنبها:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    الخلط بين الكتلة المولية للعنصر والجزيء — تذكر أن O₂ كتلته
                    32 لا 16
                  </li>
                  <li>
                    استخدام قانون الحجم المولي لغير الغازات — لا ينطبق على
                    السوائل والصلبة
                  </li>
                  <li>
                    نسيان أن قانون V = n × 22.4 يُطبّق فقط عند الشروط
                    القياسية (STP)
                  </li>
                  <li>
                    قسمة المولات على الكتلة المولية بدلاً من ضربها عند حساب
                    الكتلة
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للمستخدم المتقدم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    ادخل كتلة مولية مخصصة للسبائك أو المركبات غير المدرجة
                  </li>
                  <li>
                    استخدم النتائج لحساب نسبة المتفاعلات والمنتجات في
                    المعادلات الكيميائية
                  </li>
                  <li>
                    حوّل مولات المحلول باستخدام معادلة التركيز: c = n ÷ V(لتر)
                  </li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تنبيه:</strong> الكتلة المولية الصحيحة أساس كل
                  الحسابات — خطأ بسيط فيها ينتج نتائج مغلوطة تماماً. راجع
                  الجدول الدوري أو استخدم قائمة المواد في الحاسبة.
                </div>
              </div>
            </div>
          </div>

          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما هو المول في الكيمياء؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  المول وحدة القياس الأساسية للكمية المادية في الكيمياء. يعادل
                  المول الواحد عدداً يساوي <strong>6.022 × 10²³ جسيم</strong>{" "}
                  (ذرات أو جزيئات أو أيونات)، ويُعرف هذا الرقم بعدد أفوجادرو
                  نسبةً إلى العالم الإيطالي أميديو أفوجادرو. بعبارة أبسط: كما
                  أن الدزينة تعني 12، فالمول يعني 6.022 × 10²³ — فقط بمقياس
                  ذري هائل يُمكّن الكيميائيين من "عدّ" الذرات التي لا تُرى.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف أحسب عدد المولات من الكتلة؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  القانون مباشر: <strong>عدد المولات = الكتلة ÷ الكتلة المولية</strong>.
                  أدخل كتلة المادة بالجرام، ثم اقسمها على كتلتها المولية
                  (بالجرام/مول). مثال: لديك 58.44 جرام من ملح الطعام NaCl
                  وكتلته المولية 58.44 جم/مول، إذن عدد المولات = 58.44 ÷ 58.44
                  = 1 مول بالضبط.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما الفرق بين الكتلة الذرية والكتلة المولية؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الكتلة الذرية هي كتلة ذرة واحدة مقاسةً بوحدة الكتلة
                  الذرية (amu أو Da)، بينما الكتلة المولية هي كتلة مول كامل
                  (أي 6.022 × 10²³ ذرة) مقاسةً بالجرام/مول. الرقم نفسه!
                  الكربون كتلته الذرية 12.011 amu وكتلته المولية 12.011 جم/مول.
                  هذا التوافق العبقري هو ما يجعل مفهوم المول عملياً في
                  المختبر.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  متى يُطبَّق قانون الحجم المولي 22.4 لتر؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  ينطبق قانون الحجم المولي 22.4 لتر/مول على الغازات المثالية
                  فقط، وعند الشروط القياسية تحديداً:{" "}
                  <strong>درجة حرارة 0°مئوية وضغط 1 ضغط جوي (STP)</strong>.
                  عند درجات حرارة أو ضغوط مختلفة، يُستخدم قانون الغاز المثالي
                  PV = nRT بدلاً من ذلك. لا تطبّق هذا القانون على السوائل أو
                  المواد الصلبة لأنه لا ينطبق إلا على الغازات.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف أجد الكتلة المولية لمركب كيميائي؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  اجمع الكتل الذرية لجميع ذرات المركب من الجدول الدوري. مثلاً
                  لحساب الكتلة المولية لكبريتات الصوديوم Na₂SO₄: (2 × 23) +
                  32 + (4 × 16) = 46 + 32 + 64 = <strong>142 جم/مول</strong>.
                  إذا لم تجد المادة في قائمة الحاسبة، اختر "مادة مخصصة" وأدخل
                  الكتلة المولية يدوياً.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل يمكن أن يكون عدد المولات كسراً؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  بالطبع! عدد المولات يمكن أن يكون أي رقم موجب، سواء أكان
                  صحيحاً أو كسراً أو عشرياً. على سبيل المثال، 0.5 مول من الماء
                  يعني 9 جرامات، و0.25 مول من NaCl يعني 14.61 جرام. عملياً في
                  المختبرات، كثيراً ما تتعامل مع كسور المول لأن الكميات
                  المقاسة لا تكون بالضرورة مضاعفات صحيحة.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما العلاقة بين المول والتفاعلات الكيميائية؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  المعاملات في المعادلة الكيميائية تمثّل النسبة المولية
                  للمتفاعلات والمنتجات. مثلاً في: 2H₂ + O₂ → 2H₂O، تتفاعل 2
                  مول هيدروجين مع 1 مول أكسجين لتُنتج 2 مول ماء. هذه النسب
                  تُمكّنك من حساب "المتفاعل المحدِّد" ومقدار الناتج في أي
                  تفاعل — وهو جوهر الكيمياء الكمية (Stoichiometry).
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل الحاسبة مناسبة لطلاب الثانوية والجامعة؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  نعم تماماً، الحاسبة مصممة لجميع المستويات. طلاب الثانوية
                  يستفيدون من حل مسائل "عدد المولات" الأساسية، بينما يستفيد
                  طلاب الجامعة من التحويل السريع بين الكميات المختلفة في
                  الكيمياء التحليلية والعضوية. كما يمكن لأساتذة الكيمياء
                  استخدامها للتحقق من أسئلة الامتحانات والتمارين.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default MolesCalculator;